import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WorkHistoryOutlinedIcon from '@mui/icons-material/WorkHistoryOutlined';
import { Button } from '@/shared/components/ui';
import { guideProfileApi } from '@/features/guide-profile/api/guide-profile.api';
import { GuideExperienceRange, type GuideApplicationInput } from '@/types';
import { GUIDE_EXPERIENCE_OPTIONS } from '@/features/guide-profile/constants';
import { INDIA_STATES } from '@/features/onboarding/constants';
import { legalConfig } from '@/shared/config/legal';
import { digitsOnlyNational } from '@/shared/utils/phone';
import { ReferralCodeField, type ReferralValue } from './ReferralCodeField';

const consentLabelId = 'guide-third-party-consent-label';
const workingAsGuideLabelId = 'guide-currently-working-label';

/**
 * Validation messages are translation keys, resolved only when shown — so an
 * error already on screen follows the visitor into another language.
 */
type ErrorKey = ParseKeys<['guideProfile', 'common']>;
const errorKey = (key: ErrorKey) => key;

/**
 * Mirrors `guideApplicationSchema` on the server. The server re-validates
 * everything — this exists for fast feedback, not for security.
 */
const schema = z.object({
  fullName: z
    .string()
    .trim()
    .min(2, errorKey('applicationForm.errors.fullName'))
    .max(120, errorKey('applicationForm.errors.fullNameTooLong')),
  email: z.string().trim().email(errorKey('common:validation.email')),
  phone: z
    .string()
    .trim()
    .regex(/^[6-9]\d{9}$/, errorKey('common:validation.mobileNumber')),
  state: z.string().trim().min(2, errorKey('applicationForm.errors.state')),
  city: z
    .string()
    .trim()
    .min(2, errorKey('applicationForm.errors.city'))
    .max(120, errorKey('applicationForm.errors.cityTooLong')),
  // Optional on the server for older clients; required on this form.
  currentlyWorkingAsGuide: z.enum(['yes', 'no'], {
    errorMap: () => ({ message: errorKey('applicationForm.errors.currentlyWorking') }),
  }),
  guideExperienceRange: z.nativeEnum(GuideExperienceRange, {
    errorMap: () => ({ message: errorKey('applicationForm.errors.experience') }),
  }),
  // Unchecked by default. Registration is blocked until this is accepted.
  thirdPartyConsent: z.boolean().refine((value) => value === true, {
    message: errorKey('applicationForm.errors.consent'),
  }),
});

export type GuideApplicationFormData = z.infer<typeof schema>;

interface GuideApplicationFormProps {
  onApplied: (result: { alreadyApplied: boolean }) => void;
}

/**
 * Fields are placeholder-led rather than label-led: the card already says
 * "Start your application", so a floating label on every row only repeats the
 * placeholder and doubles the vertical rhythm. The leading icon carries the
 * field's identity, and `aria-label` keeps it announced for screen readers.
 *
 * The form sits inside the hero's glass panel, so the inputs are tinted glass
 * too — a solid white field would punch holes in the panel. The palette here
 * mirrors the home hero's search card.
 */
const GLASS_BORDER = 'rgba(255, 255, 255, 0.28)';
const GLASS_PLACEHOLDER = 'rgba(255, 255, 255, 0.6)';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(255, 255, 255, 0.08)',
    borderRadius: '8px',
    color: 'common.white',
    '& fieldset': { borderColor: GLASS_BORDER },
    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
    '&.Mui-focused fieldset': { borderColor: 'common.white', borderWidth: '1px' },
    '&.Mui-error fieldset': { borderColor: 'error.light' },
  },
  // `opacity` is reset because browsers dim placeholder text by default, which
  // on a dark panel drops it below the contrast floor.
  '& input::placeholder': { color: GLASS_PLACEHOLDER, opacity: 1 },
  '& .MuiSelect-icon': { color: GLASS_PLACEHOLDER },
  '& .MuiFormHelperText-root': {
    color: 'rgba(255, 255, 255, 0.65)',
    '&.Mui-error': { color: 'error.light' },
  },
};

function StartIcon({ children }: { children: React.ReactNode }) {
  return (
    <InputAdornment position="start" sx={{ color: GLASS_PLACEHOLDER, mr: 1 }}>
      {children}
    </InputAdornment>
  );
}

export function GuideApplicationForm({ onApplied }: GuideApplicationFormProps) {
  const { t } = useTranslation(['guideProfile', 'common']);
  const errorText = (message?: string) => (message ? t(message as ErrorKey) : undefined);
  const [serverError, setServerError] = useState('');
  const [consentOpen, setConsentOpen] = useState(false);
  const [referral, setReferral] = useState<ReferralValue | null>(null);
  const [referralError, setReferralError] = useState<string | undefined>();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<GuideApplicationFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      state: '',
      city: '',
      currentlyWorkingAsGuide: undefined,
      guideExperienceRange: undefined,
      thirdPartyConsent: false,
    },
  });

  const consentGiven = watch('thirdPartyConsent');

  const applyMutation = useMutation({
    mutationFn: (data: GuideApplicationInput) => guideProfileApi.apply(data),
    onSuccess: (result) => {
      onApplied({ alreadyApplied: result.status === 'already_applied' });
    },
    onError: (error: unknown) => {
      const data = (
        error as {
          response?: { data?: { message?: string; errors?: Record<string, string[]> } };
        }
      )?.response?.data;
      const referralMessage = data?.errors?.referralCode?.[0];
      if (referralMessage) {
        setReferralError(referralMessage);
        return;
      }
      setServerError(data?.message ?? t('common:errors.generic'));
    },
  });

  const onSubmit = (data: GuideApplicationFormData) => {
    setServerError('');
    setReferralError(undefined);
    applyMutation.mutate({
      ...(referral ? { referralCode: referral.code, referralSource: referral.source } : {}),
      fullName: data.fullName,
      email: data.email,
      phone: data.phone,
      state: data.state,
      city: data.city,
      currentlyWorkingAsGuide: data.currentlyWorkingAsGuide === 'yes',
      guideExperienceRange: data.guideExperienceRange,
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Typography
        variant="body2"
        sx={{ mb: 2.5, lineHeight: 1.7, color: 'rgba(255, 255, 255, 0.72)' }}
      >
        {t('applicationForm.intro')}
      </Typography>

      {serverError && (
        <Alert
          severity="error"
          variant="outlined"
          sx={{
            mb: 2.5,
            color: 'common.white',
            bgcolor: 'rgba(220, 38, 38, 0.18)',
            borderColor: 'error.light',
            '& .MuiAlert-icon': { color: 'error.light' },
          }}
        >
          {serverError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Controller
          name="fullName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              placeholder={`${t('applicationForm.fields.fullName')} *`}
              fullWidth
              required
              autoComplete="name"
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <StartIcon>
                    <PersonOutlineIcon fontSize="small" />
                  </StartIcon>
                ),
              }}
              inputProps={{ 'aria-label': t('applicationForm.fields.fullName') }}
              error={!!errors.fullName}
              helperText={errorText(errors.fullName?.message)}
            />
          )}
        />

        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              placeholder={`${t('applicationForm.fields.email')} *`}
              type="email"
              fullWidth
              required
              autoComplete="email"
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <StartIcon>
                    <MailOutlineIcon fontSize="small" />
                  </StartIcon>
                ),
              }}
              inputProps={{ 'aria-label': t('applicationForm.fields.email') }}
              error={!!errors.email}
              helperText={errorText(errors.email?.message)}
            />
          )}
        />

        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              onChange={(event) => field.onChange(digitsOnlyNational(event.target.value))}
              placeholder={`${t('applicationForm.fields.phone')} *`}
              fullWidth
              required
              autoComplete="tel"
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <StartIcon>
                    <PhoneIphoneIcon fontSize="small" />
                    <Box component="span" sx={{ ml: 1, color: 'common.white' }}>
                      +91
                    </Box>
                  </StartIcon>
                ),
              }}
              inputProps={{ 'aria-label': t('applicationForm.fields.phone') }}
              error={!!errors.phone}
              helperText={errorText(errors.phone?.message) ?? t('applicationForm.phoneHint')}
            />
          )}
        />

        <Controller
          name="state"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              select
              fullWidth
              required
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <StartIcon>
                    <MapOutlinedIcon fontSize="small" />
                  </StartIcon>
                ),
              }}
              SelectProps={{
                displayEmpty: true,
                // Without an explicit empty render the select collapses to a
                // blank row, which reads as a broken field rather than a
                // prompt to choose.
                renderValue: (value) =>
                  value ? (
                    (value as string)
                  ) : (
                    <Box component="span" sx={{ color: GLASS_PLACEHOLDER }}>
                      {t('applicationForm.fields.state')} *
                    </Box>
                  ),
                // Must sit on `inputProps` to reach the combobox element;
                // directly on SelectProps it labels only the outer wrapper.
                inputProps: { 'aria-label': t('applicationForm.fields.state') },
              }}
              error={!!errors.state}
              helperText={errorText(errors.state?.message)}
            >
              {INDIA_STATES.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        <Controller
          name="city"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              placeholder={`${t('applicationForm.fields.city')} *`}
              fullWidth
              required
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <StartIcon>
                    <PlaceOutlinedIcon fontSize="small" />
                  </StartIcon>
                ),
              }}
              inputProps={{ 'aria-label': t('applicationForm.fields.city') }}
              error={!!errors.city}
              helperText={errorText(errors.city?.message)}
            />
          )}
        />

        <Controller
          name="currentlyWorkingAsGuide"
          control={control}
          render={({ field, fieldState }) => (
            <FormControl
              component="fieldset"
              required
              error={!!fieldState.error}
              sx={{
                width: '100%',
                border: '1px solid',
                borderColor: fieldState.error ? 'error.light' : GLASS_BORDER,
                borderRadius: '8px',
                bgcolor: 'rgba(255, 255, 255, 0.06)',
                px: 1.5,
                py: 1,
              }}
            >
              <FormLabel
                id={workingAsGuideLabelId}
                component="legend"
                sx={{
                  p: 0,
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  color: 'rgba(255, 255, 255, 0.88)',
                  // MUI recolours the legend on focus and error; on the dark
                  // panel the theme's primary/error text drops out of contrast.
                  '&.Mui-focused, &.Mui-error': { color: 'rgba(255, 255, 255, 0.88)' },
                  '& .MuiFormLabel-asterisk': { color: 'inherit' },
                }}
              >
                {t('applicationForm.currentlyWorking')}
              </FormLabel>
              <RadioGroup
                row
                name={field.name}
                value={field.value ?? ''}
                onChange={(event) => field.onChange(event.target.value)}
                onBlur={field.onBlur}
                aria-labelledby={workingAsGuideLabelId}
                sx={{
                  gap: 2,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    color: 'common.white',
                  },
                  '& .MuiRadio-root': {
                    color: 'rgba(255, 255, 255, 0.75)',
                    '&.Mui-checked': { color: 'primary.light' },
                  },
                }}
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio size="small" inputRef={field.ref} />}
                  label={t('common:answers.yes')}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio size="small" />}
                  label={t('common:answers.no')}
                />
              </RadioGroup>
              {fieldState.error?.message && (
                <FormHelperText sx={{ mx: 0, mt: 0, color: 'error.light' }}>
                  {errorText(fieldState.error.message)}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />

        <Controller
          name="guideExperienceRange"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ''}
              select
              fullWidth
              required
              sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <StartIcon>
                    <WorkHistoryOutlinedIcon fontSize="small" />
                  </StartIcon>
                ),
              }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) =>
                  value ? (
                    t(`applicationForm.experienceOptions.${value as GuideExperienceRange}`)
                  ) : (
                    <Box component="span" sx={{ color: GLASS_PLACEHOLDER }}>
                      {t('applicationForm.fields.experience')} *
                    </Box>
                  ),
                // Must sit on `inputProps` to reach the combobox element;
                // directly on SelectProps it labels only the outer wrapper.
                inputProps: { 'aria-label': t('applicationForm.fields.experience') },
              }}
              error={!!errors.guideExperienceRange}
              helperText={errorText(errors.guideExperienceRange?.message)}
            >
              {GUIDE_EXPERIENCE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {t(`applicationForm.experienceOptions.${option.value}`)}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        <ReferralCodeField
          serverError={referralError}
          onChange={(value) => {
            setReferral(value);
            setReferralError(undefined);
          }}
        />

        <Controller
          name="thirdPartyConsent"
          control={control}
          render={({ field, fieldState }) => (
            <FormControl error={!!fieldState.error} sx={{ width: '100%' }}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: fieldState.error ? 'error.light' : GLASS_BORDER,
                  borderRadius: '8px',
                  bgcolor: 'rgba(255, 255, 255, 0.06)',
                  px: 1.25,
                  py: 1.25,
                }}
              >
                <FormControlLabel
                  sx={{
                    alignItems: 'flex-start',
                    mx: 0,
                    gap: 0.75,
                    width: '100%',
                    '& .MuiCheckbox-root': {
                      p: 0,
                      mt: '2px',
                      color: 'rgba(255, 255, 255, 0.75)',
                      '&.Mui-checked': { color: 'primary.light' },
                    },
                    '& .MuiFormControlLabel-label': {
                      flex: 1,
                      minWidth: 0,
                    },
                  }}
                  control={
                    <Checkbox
                      name={field.name}
                      inputRef={field.ref}
                      onBlur={field.onBlur}
                      checked={!!field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                      size="small"
                      inputProps={{
                        'aria-labelledby': consentLabelId,
                        'aria-required': true,
                      }}
                    />
                  }
                  label={
                    <Typography
                      id={consentLabelId}
                      component="span"
                      sx={{
                        display: 'block',
                        fontSize: '0.8125rem',
                        lineHeight: 1.5,
                        color: 'rgba(255, 255, 255, 0.88)',
                      }}
                    >
                      {t('applicationForm.consent.summary')}
                    </Typography>
                  }
                />
                <Box
                  component="button"
                  type="button"
                  onClick={() => setConsentOpen(true)}
                  sx={{
                    display: 'inline-block',
                    ml: '28px',
                    mt: 0.75,
                    p: 0,
                    border: 0,
                    bgcolor: 'transparent',
                    color: 'primary.light',
                    font: 'inherit',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    lineHeight: 1.4,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textUnderlineOffset: 2,
                  }}
                >
                  {t('applicationForm.consent.readFull')}
                </Box>
              </Box>
              {fieldState.error?.message && (
                <FormHelperText sx={{ mx: 0.5, mt: 0.75, color: 'error.light' }}>
                  {errorText(fieldState.error.message)}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />

        <Button
          type="submit"
          size="large"
          fullWidth
          disabled={applyMutation.isPending || !consentGiven}
          endIcon={!applyMutation.isPending && <ArrowForwardIcon />}
          sx={{
            mt: 0.5,
            py: 1.5,
            bgcolor: 'primary.main',
            color: 'common.white',
            fontWeight: 700,
            boxShadow: '0 8px 20px rgba(37, 99, 235, 0.35)',
            '&:hover': {
              bgcolor: 'primary.dark',
              boxShadow: '0 10px 24px rgba(37, 99, 235, 0.45)',
            },
          }}
        >
          {applyMutation.isPending
            ? t('applicationForm.submitting')
            : t('applicationForm.submit')}
        </Button>
      </Box>

      <Dialog
        open={consentOpen}
        onClose={() => setConsentOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="guide-consent-dialog-title"
      >
        <DialogTitle id="guide-consent-dialog-title" sx={{ fontWeight: 700, pr: 3 }}>
          {t('applicationForm.consent.dialogTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            <Trans
              t={t}
              i18nKey="applicationForm.consent.dialogBody"
              components={{
                privacyLink: (
                  <Box
                    component={RouterLink}
                    to={legalConfig.privacyPolicyPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      textDecoration: 'underline',
                      textUnderlineOffset: 2,
                    }}
                  />
                ),
              }}
            />
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="text" onClick={() => setConsentOpen(false)}>
            {t('common:actions.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
